import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { PermitCategoryService } from '../../services/permit-category.service';
import { PermitConfigRefreshService } from '../../services/permit-config-refresh.service';
import { PermitDepartmentService } from '../../services/permit-department.service';
import { MessageService } from '../../../global/services/message/message.abstract.service';
import { BcitiConfirmDialogComponent } from '../../../global/components/dialogs/bciti-confirm-dialog/bciti-confirm-dialog.component';
import { PermitCategory, I18nString } from '../../models/permit-category.model';
import { PermitDepartment, permitDepartmentName } from '../../models/permit-department.model';
import { PermitTypeConfig } from '../../models/permit-type.model';

@Component({
    selector: 'app-permit-config-categories',
    templateUrl: './permit-config-categories.component.html',
    styleUrls: ['./permit-config-categories.component.scss'],
})
export class PermitConfigCategoriesComponent implements OnInit, OnDestroy {
    @Output() create = new EventEmitter<void>();
    @Output() edit = new EventEmitter<PermitCategory>();
    @Output() editType = new EventEmitter<{ category: PermitCategory; typeId: string }>();
    @Output() addType = new EventEmitter<PermitCategory>();

    categories: PermitCategory[] = [];
    loading = true;
    typesByCategory: { [categoryId: string]: PermitTypeConfig[] } = {};
    typesLoading: { [categoryId: string]: boolean } = {};

    // The category edition payload carries `defaultDepartment` as a bare id, so keep the
    // department list on hand to resolve names. Archived departments are included: a type can
    // still point at one, and showing its name beats showing nothing.
    private departmentsById = new Map<string, PermitDepartment>();

    private destroy$ = new Subject<void>();

    constructor(
        private categoryService: PermitCategoryService,
        private refreshService: PermitConfigRefreshService,
        private departmentService: PermitDepartmentService,
        private dialog: MatDialog,
        private translate: TranslateService,
        private messageService: MessageService,
    ) {}

    ngOnInit(): void {
        this.loadDepartments();
        this.loadCategories();
        this.refreshService.categoryChanged$.pipe(takeUntil(this.destroy$)).subscribe((categoryId) => {
            this.loadCategories();
            if (this.typesByCategory[categoryId]) {
                this.loadTypes(categoryId);
            }
        });
    }

    /**
     * Full reload, dropping the cached type lists so they are refetched too.
     *
     * `loadCategories()` alone only calls `loadTypes()` for categories whose types
     * are not already in hand — right after a local edit, wrong after a connector
     * sync, which is precisely what replaces the types and their forms. The cards
     * stay in their loading state rather than flashing "no types" while the
     * refetch is in flight.
     */
    reloadAll(): void {
        for (const categoryId of Object.keys(this.typesByCategory)) {
            this.typesLoading[categoryId] = true;
        }
        this.typesByCategory = {};
        this.loadCategories();
    }

    loadCategories(): void {
        this.loading = this.categories.length === 0;
        this.categoryService
            .getCategories()
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (categories) => {
                    this.categories = (categories || []).slice().sort((a, b) => a.displayOrder - b.displayOrder);
                    this.loading = false;
                    // Load types for all categories so cards are always expanded
                    this.categories.forEach((cat) => {
                        if (!this.typesByCategory[cat._id]) {
                            this.loadTypes(cat._id);
                        }
                    });
                },
                error: () => {
                    this.loading = false;
                    this.messageService.error(this.translate.instant('permit.config.common.error'));
                },
            });
    }

    loadTypes(categoryId: string): void {
        this.typesLoading[categoryId] = true;
        this.categoryService
            .getCategoryEdition(categoryId)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (edition) => {
                    const types = (edition.permitTypes || []) as unknown as PermitTypeConfig[];
                    this.typesByCategory[categoryId] = types.slice().sort((a, b) => a.displayOrder - b.displayOrder);
                    this.typesLoading[categoryId] = false;
                },
                error: () => {
                    this.typesLoading[categoryId] = false;
                    this.messageService.error(this.translate.instant('permit.config.common.error'));
                },
            });
    }

    i18nName(name: I18nString | string): string {
        if (!name) return '';
        if (typeof name === 'string') return name;
        const lang = this.translate.currentLang || 'fr';
        return name[lang] || name.fr || name.en || '';
    }

    onCreateCategory(): void {
        this.create.emit();
    }

    onEditCategory(category: PermitCategory): void {
        this.edit.emit(category);
    }

    onEditType(category: PermitCategory, type: PermitTypeConfig): void {
        this.editType.emit({ category, typeId: type._id });
    }

    onAddType(category: PermitCategory): void {
        this.addType.emit(category);
    }

    toggleCategoryActive(category: PermitCategory, checked: boolean): void {
        const previous = category.active;
        category.active = checked;
        this.categoryService
            .updateCategory(category._id, { active: category.active })
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                // eslint-disable-next-line @typescript-eslint/no-empty-function
                next: () => {},
                error: () => {
                    category.active = previous;
                    this.messageService.error(this.translate.instant('permit.config.common.error'));
                },
            });
    }

    confirmDeleteCategory(category: PermitCategory): void {
        const dialogRef = this.dialog.open(BcitiConfirmDialogComponent, {
            width: '400px',
            data: {
                dialog: {
                    title: this.translate.instant('permit.config.categories.delete_title'),
                    body: this.translate.instant('permit.config.categories.delete_body'),
                    confirmLabel: this.translate.instant('permit.config.common.delete'),
                    cancelLabel: this.translate.instant('permit.config.common.cancel'),
                    confirmButtonClass: 'danger-btn',
                },
                component: { data: {}, entry: null },
            },
        });

        dialogRef
            .afterClosed()
            .pipe(takeUntil(this.destroy$))
            .subscribe((confirmed) => {
                if (!confirmed) return;
                this.categoryService
                    .deleteCategory(category._id)
                    .pipe(takeUntil(this.destroy$))
                    .subscribe({
                        next: () => {
                            delete this.typesByCategory[category._id];
                            this.loadCategories();
                        },
                        error: () => this.messageService.error(this.translate.instant('permit.config.common.error')),
                    });
            });
    }

    toggleTypeActive(category: PermitCategory, type: PermitTypeConfig, checked: boolean): void {
        const previous = type.active;
        type.active = checked;
        this.categoryService
            .updatePermitType(category._id, type._id, { active: type.active })
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                // eslint-disable-next-line @typescript-eslint/no-empty-function
                next: () => {},
                error: (error) => {
                    type.active = previous;
                    const backendMessage =
                        (Array.isArray(error?.error?.messages) && typeof error.error.messages[0] === 'string' ? error.error.messages[0] : undefined) ||
                        (typeof error?.error?.message === 'string' ? error.error.message : undefined);

                    this.messageService.error(backendMessage || this.translate.instant('permit.config.common.error'));
                },
            });
    }

    confirmDeleteType(category: PermitCategory, type: PermitTypeConfig): void {
        const dialogRef = this.dialog.open(BcitiConfirmDialogComponent, {
            width: '400px',
            data: {
                dialog: {
                    title: this.translate.instant('permit.config.categories.type_delete_title'),
                    body: this.translate.instant('permit.config.categories.type_delete_body'),
                    confirmLabel: this.translate.instant('permit.config.common.delete'),
                    cancelLabel: this.translate.instant('permit.config.common.cancel'),
                    confirmButtonClass: 'danger-btn',
                },
                component: { data: {}, entry: null },
            },
        });

        dialogRef
            .afterClosed()
            .pipe(takeUntil(this.destroy$))
            .subscribe((confirmed) => {
                if (!confirmed) return;
                this.categoryService
                    .deletePermitType(category._id, type._id)
                    .pipe(takeUntil(this.destroy$))
                    .subscribe({
                        next: () => {
                            this.loadTypes(category._id);
                            this.loadCategories();
                        },
                        error: () => this.messageService.error(this.translate.instant('permit.config.common.error')),
                    });
            });
    }

    private loadDepartments(): void {
        this.departmentService
            .getDepartments({ includeInactive: true })
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (departments) => {
                    this.departmentsById = new Map((departments || []).map((department) => [department._id, department]));
                },
                // Leave the map empty: types whose department is populated still render, the rest
                // fall back to the em dash as before.
                error: () => undefined,
            });
    }

    /** Resolves the type's department whether the payload populated it or sent only its id. */
    departmentName(type: PermitTypeConfig): string {
        const dept: any = type?.defaultDepartment;
        if (!dept) return '';
        const lang = this.translate.currentLang || 'fr';
        if (typeof dept === 'string') {
            const resolved = this.departmentsById.get(dept);
            return resolved ? permitDepartmentName(resolved.name, lang) : '';
        }
        return permitDepartmentName(dept.name, lang);
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
